import * as THREE from 'three';
import { PALETTE, type PaletteName } from '@/art/palette';
import { box, concatGeometries } from '@/art/geometry';
import { emissive, surface } from '@/art/materials';
import { animateWater, buildCanalBridge, buildGround, buildQuayDeck, buildQuayWall, buildWater } from '@/meshkit/env/ground';
import { buildHarborProp, neonSignLightOffset, type HarborPropKind } from '@/meshkit/props/harbor';
import type { VehicleKind } from '@/meshkit/vehicles/cars';
import { buildStreetlamp, streetlampLightOffset } from '@/meshkit/props/streetlamp';
import type { LightPool } from '@/fx/lighting';
import type { CollisionHandle, CollisionWorld, Obstacle } from '@/world/collision';
import { buildBuilding, buildingCollider, propCollider, streetlampCollider } from '@/world/footprints';
import { CITY } from '@/world/grid';
import type { MapSemantics } from '@/world/MapSemantics';

export interface LevelData {
  readonly name: string;
  readonly ground: {
    readonly width: number;
    readonly depth: number;
    readonly x: number;
    readonly z: number;
    readonly pavements: { north?: boolean; south?: boolean; east?: boolean; west?: boolean };
  };
  readonly water?: {
    readonly width: number;
    readonly depth: number;
    readonly x: number;
    readonly z: number;
  };
  readonly quayWall?: {
    readonly length: number;
    readonly x: number;
    readonly z: number;
  };
  readonly quayDeck?: {
    readonly length: number;
    readonly depth: number;
    readonly x: number;
    readonly z: number;
  };
  readonly obstacles: readonly Obstacle[];
  readonly bridges?: readonly BridgePlacement[];
  readonly buildings: readonly BuildingPlacement[];
  readonly streetlamps: readonly StreetlampPlacement[];
  readonly props?: readonly PropPlacement[];
  readonly trafficRoutes?: readonly TrafficRoute[];
  readonly parkedVehicles?: readonly ParkedVehiclePlacement[];
  readonly semantics?: MapSemantics;
  readonly safehouse: {
    readonly x: number;
    readonly z: number;
    readonly radius?: number;
  };
}

export function animateLevelWater(root: THREE.Object3D, elapsed: number): void {
  animateWater(root, elapsed);
}

export interface BuildingPlacement {
  readonly kind?: 'warehouse' | 'canalHouse' | 'tenement' | 'church';
  readonly x: number;
  readonly z: number;
  readonly width: number;
  readonly depth: number;
  readonly floors: number;
  readonly facing: 'north' | 'south' | 'east' | 'west';
  readonly wall: PaletteName;
  readonly seed: string;
  readonly batch: string;
}

export interface StreetlampPlacement {
  readonly x: number;
  readonly z: number;
  readonly rotation: number;
}

export interface BridgePlacement {
  readonly x: number;
  readonly z: number;
  readonly rotation?: number;
}

export interface PropPlacement {
  readonly kind: HarborPropKind;
  readonly x: number;
  readonly z: number;
  readonly rotation?: number;
}

export interface TrafficRoute {
  readonly id: string;
  readonly kind: VehicleKind;
  readonly points: readonly THREE.Vector2[];
  readonly speedLimit: number;
  readonly loop: boolean;
}

export interface ParkedVehiclePlacement {
  readonly id: string;
  readonly kind: VehicleKind;
  readonly position: THREE.Vector2;
  readonly rotation: number;
  readonly stealable: boolean;
}

export interface LoadedLevel {
  readonly root: THREE.Group;
  readonly buildingCount: number;
  readonly streetlampCount: number;
  readonly batchedBuildingMeshes: number;
  readonly batchedBuildingSourceMeshes: number;
  readonly instancedStreetlampMeshes: number;
  readonly collisionHandles: readonly CollisionHandle[];
  readonly lightHandles: readonly THREE.PointLight[];
}

interface MeshBatch {
  readonly geometries: THREE.BufferGeometry[];
  readonly material: THREE.Material;
  readonly occluder: boolean;
  readonly castShadow: boolean;
  readonly receiveShadow: boolean;
}

export function loadLevel(
  scene: THREE.Scene,
  collision: CollisionWorld,
  lights: LightPool,
  data: LevelData,
): LoadedLevel {
  const root = new THREE.Group();
  root.name = data.name;
  scene.add(root);
  const collisionHandles: CollisionHandle[] = [];
  const lightHandles: THREE.PointLight[] = [];

  loadEnvironment(root, data);
  for (const obstacle of data.obstacles) collisionHandles.push(collision.add(obstacle));
  const buildingBatchStats = loadBuildings(root, collision, data, collisionHandles);
  const instancedStreetlampMeshes = loadStreetlamps(root, collision, lights, data.streetlamps, collisionHandles, lightHandles);
  loadProps(root, collision, lights, data.props ?? [], collisionHandles, lightHandles);
  loadSafehouse(root, data.safehouse);

  return {
    root,
    buildingCount: data.buildings.length,
    streetlampCount: data.streetlamps.length,
    batchedBuildingMeshes: buildingBatchStats.outputMeshes,
    batchedBuildingSourceMeshes: buildingBatchStats.sourceMeshes,
    instancedStreetlampMeshes,
    collisionHandles,
    lightHandles,
  };
}

function loadEnvironment(root: THREE.Group, data: LevelData): void {
  const ground = buildGround({
    width: data.ground.width,
    depth: data.ground.depth,
    pavements: data.ground.pavements,
  });
  ground.position.set(data.ground.x, 0, data.ground.z);
  root.add(ground);

  if (data.quayWall) {
    const quayWall = buildQuayWall(data.quayWall.length);
    quayWall.position.set(data.quayWall.x, 0, data.quayWall.z);
    root.add(quayWall);
  }

  if (data.quayDeck) {
    const quayDeck = buildQuayDeck(data.quayDeck.length, data.quayDeck.depth);
    quayDeck.position.set(data.quayDeck.x, 0, data.quayDeck.z);
    root.add(quayDeck);
  }

  if (data.water) {
    const water = buildWater(data.water.width, data.water.depth);
    water.position.x = data.water.x;
    water.position.z = data.water.z;
    root.add(water);
  }

  for (const bridgePlacement of data.bridges ?? []) {
    const bridge = buildCanalBridge();
    bridge.position.set(bridgePlacement.x, 0, bridgePlacement.z);
    bridge.rotation.y = bridgePlacement.rotation ?? 0;
    root.add(bridge);
  }
}

function loadBuildings(
  root: THREE.Group,
  collision: CollisionWorld,
  data: LevelData,
  collisionHandles: CollisionHandle[],
): { outputMeshes: number; sourceMeshes: number } {
  const batches = new Map<string, MeshBatch>();
  let sourceMeshes = 0;

  for (const placement of data.buildings) {
    const building = buildBuilding(placement);
    building.position.set(placement.x, 0, placement.z);
    building.updateMatrixWorld(true);

    building.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      sourceMeshes++;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      const material = materials[0]!;
      const kind = placement.kind ?? 'warehouse';
      const scope = object.userData.naOccluder === true ? placement.seed : placement.batch;
      const key = `${scope}:${kind}:${object.name}:${material.uuid}`;
      let batch = batches.get(key);
      if (!batch) {
        batch = {
          geometries: [],
          material,
          occluder: object.userData.naOccluder === true,
          castShadow: object.castShadow,
          receiveShadow: object.receiveShadow,
        };
        batches.set(key, batch);
      }
      batch.geometries.push(object.geometry.clone().applyMatrix4(object.matrixWorld));
    });

    const collider = buildingCollider(placement);
    collisionHandles.push(collision.add({
      x: placement.x + collider.offsetX,
      z: placement.z + collider.offsetZ,
      halfX: collider.halfX,
      halfZ: collider.halfZ,
      height: collider.height,
    }));
  }

  for (const [key, batch] of batches) {
    const mesh = new THREE.Mesh(concatGeometries(batch.geometries, true), batch.material);
    mesh.name = `NA_Batch_Buildings_${key}`;
    mesh.castShadow = batch.castShadow;
    mesh.receiveShadow = batch.receiveShadow;
    if (batch.occluder) mesh.userData.naOccluder = true;
    root.add(mesh);
  }

  return { outputMeshes: batches.size, sourceMeshes };
}

function loadStreetlamps(
  root: THREE.Group,
  collision: CollisionWorld,
  lights: LightPool,
  placements: readonly StreetlampPlacement[],
  collisionHandles: CollisionHandle[],
  lightHandles: THREE.PointLight[],
): number {
  if (placements.length === 0) return 0;

  const prototype = buildStreetlamp();
  const shell = prototype.getObjectByName('shell');
  const lens = prototype.getObjectByName('lens');
  if (!(shell instanceof THREE.Mesh) || !(lens instanceof THREE.Mesh)) {
    throw new Error('Streetlamp prototype missing shell or lens mesh');
  }

  const shellMesh = new THREE.InstancedMesh(shell.geometry, shell.material, placements.length);
  shellMesh.name = 'NA_Instanced_Streetlamp_Shell';
  shellMesh.castShadow = true;
  root.add(shellMesh);

  const lensMesh = new THREE.InstancedMesh(lens.geometry, lens.material, placements.length);
  lensMesh.name = 'NA_Instanced_Streetlamp_Lens';
  root.add(lensMesh);

  const matrix = new THREE.Matrix4();
  const lightOffset = streetlampLightOffset();
  const lightPosition = new THREE.Vector3();
  const basePosition = new THREE.Vector3();
  const rotationQuaternion = new THREE.Quaternion();
  const rotationEuler = new THREE.Euler();
  const scale = new THREE.Vector3(1, 1, 1);

  placements.forEach((placement, index) => {
    rotationEuler.set(0, placement.rotation, 0);
    basePosition.set(placement.x, 0, placement.z);
    rotationQuaternion.setFromEuler(rotationEuler);
    matrix.compose(basePosition, rotationQuaternion, scale);
    shellMesh.setMatrixAt(index, matrix);
    lensMesh.setMatrixAt(index, matrix);

    lightPosition
      .copy(lightOffset)
      .applyEuler(rotationEuler)
      .add(basePosition);
    lightHandles.push(lights.add(lightPosition.clone(), PALETTE.sodiumAmber, 18, 14));

    const collider = streetlampCollider(placement.rotation);
    collisionHandles.push(collision.add({
      x: placement.x + collider.offsetX,
      z: placement.z + collider.offsetZ,
      halfX: collider.halfX,
      halfZ: collider.halfZ,
      height: collider.height,
    }));
  });

  shellMesh.instanceMatrix.needsUpdate = true;
  lensMesh.instanceMatrix.needsUpdate = true;
  return 2;
}

function loadProps(
  root: THREE.Group,
  collision: CollisionWorld,
  lights: LightPool,
  placements: readonly PropPlacement[],
  collisionHandles: CollisionHandle[],
  lightHandles: THREE.PointLight[],
): void {
  const neonOffset = neonSignLightOffset();
  const lightPosition = new THREE.Vector3();
  const rotation = new THREE.Euler();

  for (const placement of placements) {
    const prop = buildHarborProp(placement.kind);
    const y = placement.kind === 'boat' ? CITY.waterLevel : 0;
    prop.position.set(placement.x, y, placement.z);
    prop.rotation.y = placement.rotation ?? 0;
    root.add(prop);

    const collider = propCollider(placement.kind, placement.rotation ?? 0);
    if (collider) {
      collisionHandles.push(collision.add({
        x: placement.x + collider.offsetX,
        z: placement.z + collider.offsetZ,
        halfX: collider.halfX,
        halfZ: collider.halfZ,
        height: collider.height,
      }));
    }

    if (placement.kind === 'neonSign') {
      rotation.set(0, prop.rotation.y, 0);
      lightPosition.copy(neonOffset).applyEuler(rotation).add(prop.position);
      lightHandles.push(lights.add(lightPosition.clone(), PALETTE.neonRed, 8, 10));
    }
  }
}

function loadSafehouse(root: THREE.Group, safehouse: LevelData['safehouse']): void {
  const safehouseMarker = new THREE.Group();
  safehouseMarker.name = 'NA_Mission_Safehouse_Entrance';
  safehouseMarker.position.set(safehouse.x, 0, safehouse.z);
  const mat = surface('oxideMaroon');
  const glow = emissive('sodiumAmber', 1.15);
  const matMesh = new THREE.Mesh(box(2.5, 0.05, 1.1), mat);
  matMesh.position.y = 0.03;
  safehouseMarker.add(matMesh);
  const sign = new THREE.Mesh(box(1.4, 0.45, 0.08), glow);
  sign.position.set(0, 1.65, -0.15);
  safehouseMarker.add(sign);
  root.add(safehouseMarker);
}
